// API-Contract: die Draht-Typen (DTOs + Antwort-Hüllen), die Backend UND Frontend teilen.
// Das Backend bindet seine DB-Zeilen gegen diese DTOs (apps/backend/src/contract-check.ts) — eine
// Vertragsänderung (umbenanntes/umtypisiertes Feld) wird so zum TYP-Fehler, nie zu stiller Drift.
// Das Frontend konsumiert ausschließlich diese Typen (apps/frontend/src/api).

import type { AccessLevel, BillingInterval, BillingStatus, PlanTier } from './billing'
import type { VatRateGroup } from './money'
import type { WorkshopStatus } from './operator'
import type { Right } from './permissions'
import type {
  AbsenceEffect,
  ArbZgFlag,
  ArbZgFlagType,
  AttStatus,
  AttendanceDay,
  AverageGuardResult,
  StampKind,
  StampSource,
  TimesheetWeek,
  WorkPlanDay,
} from './time'

/** Sanitierter Nutzer (nie Hash/Salt/2FA-Secret) — Ausgabe von publicUser() im Backend. */
export interface PublicUserDTO {
  id: number
  name: string
  username: string
  email: string | null
  role: string
  position: string
  color: string
  /** Alt-Caps ODER V2-Rechte (Bridge-Zwischenzustand) — das Frontend löst beide über bridgedRights auf. */
  permissions: Right[]
  isSuperadmin: boolean
  workshopId: number | null
  totpEnabled: boolean
  /** true, sobald die hinterlegte E-Mail per Code bestätigt wurde (schaltet den E-Mail-Passwort-Reset frei). */
  emailVerified: boolean
}

/**
 * Kompakte Nutzer-Zeile für die Team-Liste (GET /users). Welche Felder erscheinen, hängt vom Recht des
 * Anfragenden ab (Alt-Parität listUsers): Basis immer; `permissions` nur für Zuweisungs-Rollen
 * (annahme|werkstatt|planung|chef|team); `username`/`email`/`role` nur für Team-Manager (cap `team`/Superadmin).
 * Niemals passwordHash/salt/totpSecret. Reine Anzeige — Schreibpfade nutzen PublicUserDTO/die Zeile.
 */
export interface UserListItemDTO {
  id: number
  name: string
  position: string | null
  color: string
  active: number
  permissions?: Right[]
  username?: string
  email?: string | null
  role?: string
  /** Geburtsdatum 'YYYY-MM-DD' (Phase-1/A2 Jugendschutz: <18 → JArbSchG-Wächter). Chef-only, optional. */
  birthdate?: string | null
  /** Urlaubskonto (2a): Zehnteltage als Integer (300 = 30 Tage); nur mit team_verwalten gelistet/patchbar. */
  vacationDaysTenths?: number | null
  vacationCarryTenths?: number
  entryDate?: string | null
  /** Schutz-Schalter (Phase 2/3, chef-only): mutterschutz ist ein Art.-9-Gesundheitsdatum. */
  beschaeftigungsart?: string
  stundenlohnCents?: number | null
  mutterschutz?: number | boolean
  entbindungstermin?: string | null
  /** Ausbildungsverhältnis (Azubi-Block, chef-only wie entryDate): Datums-Anker für § 15 BBiG. Steuert KEINE
   *  JArbSchG-Mengengrenze — die hängt allein am Alter. */
  ausbildungVon?: string | null
  ausbildungBis?: string | null
  /** DATEV-Fundament (Stufe A, chef-only): Personalnummer des Lohnsystems. */
  personalnummer?: string | null
}
export interface UsersListResponse {
  users: UserListItemDTO[]
}

/** Auftrag, wie ihn die API liefert (= Drizzle-Zeile orders, inkl. version). */
export interface OrderDTO {
  id: number
  nr: string | null
  customerId: number | null
  vehicleId: number | null
  mechanicId: number | null
  status: string
  prio: string
  note: string | null
  estimateStatus: string
  dueDay: number | null
  createdAt: string
  updatedAt: string
  finishedAt: string | null
  mileage: number | null
  approvalToken: string | null
  approvedAt: string | null
  scheduledDate: string | null
  startMin: number | null
  endMin: number | null
  /** Arbeitsplatz (1..bays) auf der Belegungs-Plantafel; null = nicht zugeordnet. Optional (nicht required),
   *  bis der Frontend-Handoff die eingefrorenen Fixtures nachzieht — der Server liefert das Feld immer. */
  bay?: number | null
  workshopId: number | null
  clientUuid: string | null
  approvalExpires: string | null
  archived: number
  invNo: string | null
  invSeq: number | null
  invYear: number | null
  invoicedAt: string | null
  paidAt: string | null
  paidMethod: string | null
  version: number
}

/**
 * Auftrags-LISTEN-Zeile: OrderDTO + denormalisierte Anzeigefelder, die die Karten/Listen ohne N+1 brauchen.
 * Per JOIN/Aggregat im Repo befüllt (Kennzeichen, Kunde, Mechaniker, Aufgaben-Fortschritt, gebuchte Zeit,
 * Teile-/Positions-Zähler). Reine Anzeigefelder — die schreibenden Routen nutzen weiter OrderDTO/die Zeile.
 */
export interface OrderListItemDTO extends OrderDTO {
  plate: string | null
  vehicleModel: string | null
  customerName: string | null
  customerPhone: string | null
  mechanicName: string | null
  mechanicColor: string | null
  /** Σ gebuchte Sekunden über alle time_entries des Auftrags. */
  timeSeconds: number
  taskTotal: number
  taskDone: number
  /** Σ price_cents*qty der Teile (ganze Cent). */
  partsCents: number
  partsCount: number
  /** Σ price_cents*qty der Leistungs-Positionen (ganze Cent). */
  itemsCents: number
  itemsCount: number
}

/** Aufgabe eines Auftrags (= Drizzle-Zeile tasks). `done` ist 0/1 (Integer wie in der DB). */
export interface TaskDTO {
  id: number
  orderId: number
  text: string
  done: number
  position: number
  createdAt: string | null
}
/** Teil/Ersatzteil eines Auftrags (= Drizzle-Zeile parts). Geld in ganzen Cent.
 *  `partId` verweist auf das Lager-Stammteil (inventory_parts), wenn die Position aus dem Lager kommt
 *  (NULL = frei eingetipptes Einmal-Teil). `priceCents` ist der Preis-SNAPSHOT, keine Live-Referenz. */
export interface PartDTO {
  id: number
  orderId: number
  partNumber: string | null
  name: string
  priceCents: number
  /** USt-Satz dieser Position in Prozent (19/7/0) — Snapshot, je Zeile abrechenbar. */
  vatRate: number
  qty: number
  status: string
  addedBy: number | null
  partId: number | null
  createdAt: string
}
/** Leistungs-/Lohn-Position eines Auftrags (= Drizzle-Zeile order_items). Geld in ganzen Cent. */
export interface OrderItemDTO {
  id: number
  orderId: number
  name: string
  qty: number
  priceCents: number
  /** USt-Satz dieser Position in Prozent (19/7/0) — Snapshot aus dem Katalog bzw. frei gesetzt. */
  vatRate: number
  serviceId: number | null
  createdAt: string | null
}

// ---- Antwort-Hüllen (zentraler Result-Vertrag) ----
export interface ErrorResponse {
  error: string
}
/** Versions-/Zustands-Konflikt (409): aktueller Server-Stand + Version für die UI-Reconciliation. */
export interface ConflictResponse {
  error: string
  conflict: true
  order?: OrderDTO
  version: number
}

/**
 * Abo-Zugriffsampel der Werkstatt des eingeloggten Nutzers — der ALLE-Rollen-Auszug des Abo-Zustands (für das
 * globale Banner + den Nur-Lese-Modus). Bewusst OHNE Kosten/Seats: die bleiben chef-only in GET /billing/status.
 * `level` steuert das clientseitige Gating (der Server erzwingt Schreibsperren ohnehin zentral im Router).
 */
export interface StatusAccess {
  level: AccessLevel
  /** Feiner Abo-Status; null bei Bestandswerkstatt ohne Abo-Zeile (dann level='full'). */
  billingStatus: BillingStatus | null
  /** Testphasen-Ende (DB-UTC 'YYYY-MM-DD HH:MM:SS') — für den „Testphase endet in X Tagen"-Hinweis. */
  trialEnd: string | null
  /** Beginn des Zahlungsausfalls (DB-UTC) — Anker der Kulanzfrist; null außerhalb 'past_due'. */
  pastDueSince: string | null
}

export interface StatusResponse {
  needsSetup: boolean
  user: PublicUserDTO | null
  /** Aktuelle AVV-Dokumentversion (für Re-Consent-Vergleich). */
  avvVersion?: string
  /** true, wenn die Werkstatt des eingeloggten Nutzers eine veraltete/fehlende AVV-Version hat → Re-Consent-Prompt. */
  avvOutdated?: boolean
  /** Gesetzt, wenn der Betreiber gerade per X-Impersonate-Workshop-Id in dieser Werkstatt arbeitet
   *  (Support-Modus). Der Frontend-Banner + die Navigation hängen daran. */
  impersonatingWorkshop?: { id: number; name: string } | null
  /** Abo-Zugriffsampel der Werkstatt (alle Rollen). Fehlt bei Superadmin / ohne Werkstatt / Support-Modus. */
  access?: StatusAccess
}
export interface LoginRequest {
  username: string
  password: string
  /** TOTP- oder Recovery-Code (zweiter Schritt bei 2FA-Konten). */
  totp?: string
}
export interface LoginResponse {
  user: PublicUserDTO
}
/** Passwort korrekt, aber Konto hat 2FA → der Client fragt jetzt den Code ab und sendet ihn an dieselbe Route. */
export interface LoginChallengeResponse {
  needTotp: true
}
/**
 * Passwort (+2FA) korrekt, aber die PFLICHT-E-Mail (Selbstregistrierung) ist noch nicht verifiziert → KEINE Session.
 * Der Client fragt den 6-stelligen Code ab (POST /verify-email) bzw. lässt ihn neu senden (POST /verify-email/resend).
 * Wird sowohl von /login (Resume) als auch von /register zurückgegeben.
 */
export interface EmailVerifyChallengeResponse {
  needEmailVerify: true
  email: string | null
}
/** E-Mail-Pflicht-Verifizierung einlösen (Selbstregistrierung): Login (Benutzername/E-Mail) + 6-stelliger Code. */
export interface VerifyEmailRequest {
  login: string
  code: string
}
/** Neuen Bestätigungscode anfordern (Antwort immer 200, keine Existenz-Auskunft). */
export interface ResendVerificationRequest {
  login: string
}
/** Eigene E-Mail (neu/geändert) hinterlegen (eingeloggt) — löst den Versand eines Bestätigungscodes aus. */
export interface SetEmailRequest {
  email: string
}
/** Eigene E-Mail per Code bestätigen (eingeloggt). */
export interface VerifyOwnEmailRequest {
  code: string
}
/** Eigenes Passwort ändern (Selbstservice): altes verifizieren, neues setzen (min. 8). */
export interface ChangePasswordRequest {
  old: string
  new: string
}
/** Eigenen Anzeigenamen ändern (Selbstservice). Nur App-intern sichtbar → frei wählbar. */
export interface ChangeNameRequest {
  name: string
}
/** Eigenen Benutzernamen ändern (Selbstservice). Wird kleingeschrieben; muss global eindeutig sein (sonst 400). */
export interface ChangeUsernameRequest {
  username: string
}
/** 2FA-Enrollment Schritt 1: Secret + otpauth-URL (QR) — noch nicht scharf. */
export interface TwofaSetupResponse {
  secret: string
  otpauth: string
}
export interface TwofaEnableRequest {
  code: string
}
/** 2FA-Enrollment Schritt 2 bestätigt → Recovery-Codes (Klartext, EINMALIG angezeigt). */
export interface TwofaEnableResponse {
  recovery: string[]
}
export interface TwofaDisableRequest {
  password: string
}
/** Ersteinrichtung (erster Chef, nur bei komplett leerer Nutzertabelle). Legt Werkstatt + Chef an, loggt ein. */
export interface SetupRequest {
  name: string
  username: string
  password: string
  workshop_name?: string
  email?: string
}
/** Selbstregistrierung (Test-Tarif). Legt Werkstatt + Chef an, loggt ein. AVV-Zustimmung Pflicht. */
export interface RegisterRequest {
  workshop_name: string
  chef_name: string
  username: string
  password: string
  email?: string
  avv_accepted: boolean
}
/** Passwort-Reset anfordern (Login = Benutzername ODER E-Mail). Antwort immer 200 (keine Existenz-Auskunft). */
export interface RequestResetRequest {
  login: string
}
/** Passwort-Reset einlösen: Token (aus dem Reset-Link) + neues Passwort. */
export interface ResetPasswordRequest {
  token: string
  password: string
}

// ---- Onboarding per Einmal-ID (auf bestehender Auth) ----
/** Chef erzeugt einen Onboarding-Einmal-Code: Position (Rang < eigener) + Rechte (Teilmenge der eigenen). */
export interface CreateOnboardCodeRequest {
  position: string
  permissions: Right[]
}
/**
 * Team-PATCH `/users/:id` (Chef/Zweitchef bearbeitet einen NIEDRIGERrangigen Mitarbeiter). Alle Felder optional
 * (nur Gesetztes wird geändert). Der Server erzwingt canManageUser (actorRank > targetRank) UND re-filtert
 * `permissions` über grantableRights (Floor + eigene Teilmenge) — der Client darf also nichts Über-Rangiges setzen.
 */
export interface UpdateUserRequest {
  name?: string
  position?: string
  permissions?: Right[]
  /** Geburtsdatum 'YYYY-MM-DD' (Jugendschutz); null/'' löscht es. Unter 18 schaltet der Wächter auf JArbSchG um. */
  birthdate?: string | null
  /** Urlaubskonto (2a): Zehnteltage als Integer (300 = 30 Tage); nur mit team_verwalten gelistet/patchbar. */
  vacationDaysTenths?: number | null
  vacationCarryTenths?: number
  entryDate?: string | null
  /** Schutz-Schalter (Phase 2/3, chef-only): mutterschutz ist ein Art.-9-Gesundheitsdatum. */
  beschaeftigungsart?: string
  stundenlohnCents?: number | null
  mutterschutz?: number | boolean
  entbindungstermin?: string | null
  /** Ausbildungsverhältnis (Azubi-Block, chef-only wie entryDate): Datums-Anker für § 15 BBiG. Steuert KEINE
   *  JArbSchG-Mengengrenze — die hängt allein am Alter. */
  ausbildungVon?: string | null
  ausbildungBis?: string | null
  /** DATEV-Fundament (Stufe A, chef-only): Personalnummer des Lohnsystems. */
  personalnummer?: string | null
  /** Deaktivieren (false) wirkt SOFORT (Zugang gesperrt, Sessions gekappt); Reaktivieren (true) sofern Tarif-Limit frei. */
  active?: boolean
}
/** Antwort: Klartext-Code GENAU EINMAL (danach liegt nur der Hash in der DB) + zugewiesene Rolle/Rechte + Ablauf. */
export interface CreateOnboardCodeResponse {
  code: string
  expiresAt: string
  position: string
  permissions: Right[]
}
/** Mitarbeiter löst den Onboarding-Code ein und wählt Benutzername + Passwort selbst (E-Mail bleibt NULL). */
export interface RedeemOnboardRequest {
  code: string
  username: string
  password: string
  /** Optionaler Anzeigename; fehlt er, wird der Benutzername verwendet. */
  name?: string
}
/** Chef erzeugt einen Reset-Einmal-Code für einen Mitarbeiter SEINER Werkstatt (userId aus der Team-Liste). */
export interface CreateResetCodeRequest {
  userId: number
}
export interface CreateResetCodeResponse {
  code: string
  expiresAt: string
  userId: number
}
/** Mitarbeiter löst den Reset-Code ein und setzt ein neues Passwort (min. 8). */
export interface RedeemResetCodeRequest {
  code: string
  password: string
}
/** Auftragsliste (Board/Annahme/Meine-Liste) — denormalisierte Karten-Zeilen, kein N+1. */
export interface OrdersResponse {
  orders: OrderListItemDTO[]
}
export interface OrderResponse {
  order: OrderDTO
}
/** Auftrags-Detail: der Auftrag + seine Kinder (Aufgaben/Teile/Positionen). */
export interface OrderDetailResponse {
  order: OrderDTO
  tasks: TaskDTO[]
  parts: PartDTO[]
  items: OrderItemDTO[]
}

/** Zeiteintrag, wie ihn die API liefert (= Drizzle-Zeile time_entries, inkl. version). */
export interface TimeEntryDTO {
  id: number
  orderId: number
  userId: number | null
  seconds: number
  createdAt: string
  /** A3 Timer-Gate: Startzeitpunkt des Timer-Laufs (DB-UTC); NULL = Alt-Client/unbekannt. */
  startedAt?: string | null
  clientUuid: string | null
  version: number
}
/**
 * Zeitbuchung an einem Auftrag. `client_uuid` macht den Schreibvorgang idempotent: ein über die
 * Offline-Outbox wiederholter Request legt KEINE Doppelbuchung an (DB-UNIQUE auf (order_id, client_uuid)).
 */
export interface AddTimeRequest {
  seconds: number
  client_uuid?: string
  /** A3 Timer-Gate: Startzeitpunkt des Timer-Laufs (epoch ms, wie stamped_at) — Basis der serverseitigen
   *  Anwesenheits-Deckungsprüfung. Fehlt er (Alt-Client), nimmt der Server „endet beim Empfang" an. */
  started_at?: number
}
export interface AddTimeResponse {
  entry: TimeEntryDTO
}
/** Alle Zeiteinträge eines Auftrags + ihre Summe (für die gebuchte Gesamtzeit in der Detail-Ansicht). */
export interface OrderTimeResponse {
  entries: TimeEntryDTO[]
  totalSeconds: number
}

// ---- Anwesenheit / Stempeluhr ----
/** Eigener Anwesenheitsstand (Header-Stempeluhr). `liveSince` ist server-persistiert — der Live-Zähler
 *  rechnet `todaySeconds + (now - liveSince)`, NIE aus einer im RAM gehaltenen Render-Uhr. */
export interface AttendanceState {
  status: AttStatus
  /** OCC-Lese-Token (höchste Stempel-Version); jeder Stempel muss ihn mitschicken. */
  version: number
  /** Heute (Lokaltag) abgeschlossene Arbeitssekunden, an der lokalen Mitternacht gesplittet (B-5). */
  todaySeconds: number
  /** epoch ms: ab hier live in „heute" mitzählen (auf lokale Mitternacht geklemmt); null außer bei Status 'an'. */
  liveSince: number | null
  /** Server-Zeit (epoch ms) im Moment der Antwort — der Client erkennt daraus die Geräteuhr-Abweichung (R2/1.3). */
  serverNow?: number
}
/** Stempel-Request — `version` (OCC) Pflicht, `client_uuid` macht ihn offline-/retry-idempotent. */
export interface StampRequest {
  kind: StampKind
  version: number
  client_uuid?: string
  /** Client-Stempelzeit (epoch ms), beim Enqueue in die Outbox gesetzt (Direktversuch = Replay identisch). Der
   *  SERVER leitet daraus source ab (Live ≤5 min / Nachzügler ≤72 h / Korrekturantrag darüber) — nie der Client. */
  stamped_at?: number
}
/** Versions-/Zustands-Konflikt beim Stempeln (409): aktueller Stand für die UI-Reconciliation. */
export interface AttendanceConflictResponse {
  error: string
  conflict: true
  state: AttendanceState
}
/** Umleitungs-Antwort (202) eines Nachzügler-/Backdatier-Stempels, der NICHT direkt gebucht werden konnte
 *  (Sequenz-Bruch oder > 72 h): der Server hat stattdessen einen Auto-Korrekturantrag erzeugt. Der Client
 *  entfernt das Outbox-Item (Erfolg, kein Dead-Letter) und zeigt „als Korrektur eingereicht"; `state` bleibt
 *  der UNVERÄNDERTE Anwesenheitsstand (die UI springt nicht fälschlich auf 'an'). R2/1.4. */
export interface StampConvertedResponse {
  converted: 'correction'
  correctionId: number
  state: AttendanceState
}
/** Ein Mitarbeiter in der Team-Tagesübersicht (?scope=today). */
export interface AttendanceTodayEntry {
  userId: number
  name: string
  color: string
  status: AttStatus
  todaySeconds: number
  liveSince: number | null
}
export interface AttendanceTodayResponse {
  entries: AttendanceTodayEntry[]
}

// ---- Zeitnachweis / Report (W9.2) ----
/** Ein Stempel im Zeitnachweis — inkl. ENTWERTETER (Historie/Revisionssicherheit) + Korrektur-Metadaten. */
export interface AttendanceReportStamp {
  id: number
  kind: StampKind
  /** DB-UTC-Zeitstempel 'YYYY-MM-DD HH:MM:SS'. */
  ts: string
  /** epoch ms (bequem für die UI; aus ts abgeleitet). */
  tsMs: number
  voided: boolean
  correctedBy: number | null
  replacesId: number | null
  reason: string | null
  voidBy: number | null
  voidAt: string | null
  /** R2/1.4: echte Client-Stempelzeit bei Offline-Nachzüglern ('YYYY-MM-DD HH:MM:SS'); null bei Live-Stempeln. */
  stampedAt?: string | null
  /** Maßgeblicher Ereigniszeitpunkt = coalesce(stamped_at, ts); Report-Tageszuordnung/Sortierung laufen hierauf. */
  effectiveTs?: string
  /** Provenienz: 'live' | 'outbox_replay' | 'nachtrag'. */
  source?: StampSource
  /** true, wenn der Stempel verspätet (offline) übertragen wurde (source='outbox_replay' && stampedAt≠ts). */
  offlineDelayed?: boolean
}
/** Chef-/Eigen-Zeitnachweis (`GET /attendance/report`): Tageszeilen (Beginn/Ende/Dauer) + Roh-Stempel-Historie. */
export interface AttendanceReportResponse {
  user: { id: number; name: string; position: string | null }
  /** Angefragter Zeitraum (lokale Kalendertage, inklusiv). */
  from: string
  to: string
  /** Tageszeilen im Zeitraum (nur Tage mit Stempeln/Arbeitszeit; B-5-Mitternachts-Split). */
  days: AttendanceDay[]
  /** Alle Stempel des Zeitraums inkl. entwerteter (lokaltag-gefiltert), älteste zuerst. */
  stamps: AttendanceReportStamp[]
  /** R4/1.8: erkannte ArbZG-Verstöße (schicht-basiert: §3 >10h, §4 Pause zu kurz, §5 Ruhezeit <11h). Rein hinweisend. */
  flags?: ArbZgFlag[]
  /** Phase 2: GENEHMIGTE Abwesenheiten im Zeitraum — der Nachweis zeigt sie als Tages-Badge. */
  absences?: AbsenceDTO[]
}
/** `POST /attendance/arbzg-ack` (Recht arbeitszeit:bearbeiten): quittiert einen ArbZG-Verstoß einer Schicht (R4/1.10). */
export interface ArbzgAckRequest {
  userId: number
  /** Schicht-Startzeit (epoch ms) aus dem Flag — der Server verankert die Quittierung darauf. */
  shiftStartMs: number
  type: ArbZgFlagType
  note?: string
}

/** `POST /attendance/correct` (W9.1, nur Chef/Zweitchef): revisionssichere Anwesenheits-Korrektur.
 *  `void` entwertet einen Stempel (Original bleibt, nur markiert), `add` trägt einen fehlenden Stempel
 *  als NEUEN Eintrag nach. `reason` ist Pflicht (≤300 Zeichen, bleibt dauerhaft dokumentiert).
 *  `ts` MUSS ein voller ISO-8601-Zeitstempel mit Zeitzone sein (der Server parst per Date.parse). */
export type CorrectAttendanceRequest =
  | { action: 'void'; stampId: number; reason: string }
  | { action: 'add'; userId: number; kind: StampKind; ts: string; reason: string; replacesId?: number | null }

// ---- Mitarbeiter-Korrekturanfragen (W9.3) ----
/** Eine Korrekturanfrage (= time_corrections-Zeile + denormalisierter user_name aus dem JOIN). */
export interface CorrectionDTO {
  id: number
  userId: number
  /** Name des antragstellenden Mitarbeiters (JOIN users). */
  userName: string
  message: string
  /** 'offen' | 'erledigt' | 'abgelehnt'. */
  status: string
  kind: string | null
  corrDay: string | null
  corrTime: string | null
  createdAt: string
  resolvedAt: string | null
  /** R3/1.7: strukturierter Nachtrag-Vorschlag — proposed_kind (StampKind) + proposed_at (DB-UTC), aus dem der Chef
   *  bei Freigabe den Stempel materialisiert. Auto-Anträge (Nachzügler) füllen sie; manuelle Alt-Anträge (noch) null. */
  proposedKind?: string | null
  proposedAt?: string | null
  /** R3/1.7: nach Freigabe gesetzt — id des materialisierten Stempels (Verkettung) + Chef-Entscheidungsgrund. */
  resultingStampId?: number | null
  decisionReason?: string | null
}

// ===== Benachrichtigungen (In-App-Zentrale + Push-Kanal) =====
// EIN Modell, zwei Zustellkanäle (docs/DESIGN-benachrichtigungen.md). Jede Benachrichtigung gehört genau
// einem Nutzer und einem Nav-Bereich (`area`) — daraus speisen sich Glocken-Badge und Reiter-Badges.
export interface NotificationDTO {
  id: number
  /** Ereignistyp (z. B. 'correction_submitted' | 'correction_resolved' | 'arbzg_flag' | 'arbzg_break_due'). */
  type: string
  /** Nav-Bereich für den Reiter-Badge (z. B. 'zeiten', 'auftraege'). */
  area: string
  title: string
  body: string
  /** Deep-Link-Ziel (Route inkl. optionaler Query), z. B. '/zeiten?tab=antraege'. null = kein Sprungziel. */
  target: string | null
  createdAt: string
  /** null = ungelesen. */
  readAt: string | null
}

/** GET /notifications — die eigene Liste + Ungelesen-Zähler (gesamt + je Bereich für die Reiter-Badges). */
export interface NotificationsResponse {
  items: NotificationDTO[]
  unreadTotal: number
  unreadByArea: Record<string, number>
}

/** POST /notifications/read — `all` markiert alle eigenen, sonst die genannten ids. Idempotent. */
export interface MarkNotificationsReadRequest {
  all?: boolean
  ids?: number[]
  /** Offline-Outbox-Idempotenz; serverseitig unkritisch (die Operation ist von Natur aus idempotent). */
  client_uuid?: string
}

export interface MarkNotificationsReadResponse {
  ok: true
  unreadTotal: number
  unreadByArea: Record<string, number>
}

// ===== OS-Push-Kanal (Web Push) =====
/** GET /push/vapid-public-key — öffentlicher VAPID-Key (base64url) oder null, wenn Push nicht konfiguriert ist. */
export interface VapidKeyResponse {
  key: string | null
}

/** POST /push/subscribe — die vom Browser gelieferte Subscription (endpoint + keys). */
export interface PushSubscribeRequest {
  endpoint: string
  keys: { p256dh: string; auth: string }
  /** Offline-Outbox-Idempotenz (serverseitig unkritisch: saveSubscription upsertet auf endpoint). */
  client_uuid?: string
}

/** POST /push/unsubscribe — Gerät abmelden. */
export interface PushUnsubscribeRequest {
  endpoint: string
}
export interface CorrectionsResponse {
  corrections: CorrectionDTO[]
}
/** `POST /corrections` (jeder eingeloggte Nutzer für sich): bei kind≠'sonstiges' ist corr_time Pflicht,
 *  bei 'sonstiges' eine Freitext-Beschreibung. */
export interface CreateCorrectionRequest {
  message?: string
  /** Eine der CORRECTION_KINDS; Unbekanntes/leer → 'sonstiges'. */
  kind?: string
  corr_day?: string | null
  corr_time?: string | null
}
/** `PATCH /corrections/:id` (nur Chef/Zweitchef): Anfrage abschließen. */
export interface ResolveCorrectionRequest {
  status?: 'erledigt' | 'abgelehnt'
}

// ---- Soll/Ist-Wochennachweis (W9.4) ----
/** `GET /timesheet`: pro-Nutzer Soll/Ist je Kalenderwoche im Zeitraum. Eigener für jeden; fremde nur isBoss.
 *  (v2-Neugestaltung ggü. der Alt-Team-7-Tage-Übersicht — diese Rolle übernimmt jetzt /stats.) */
export interface TimesheetResponse {
  user: { id: number; name: string; position: string | null }
  from: string
  to: string
  weeks: TimesheetWeek[]
  /** Σ über alle Wochen (Kopfzeile). */
  totalSollSeconds: number
  totalIstSeconds: number
  /** Phase 2: GENEHMIGTE Abwesenheiten, die den Zeitraum schneiden (ihre Wirkung steckt bereits in soll/ist). */
  absences?: AbsenceDTO[]
  /** Tage, an denen eine ganztägige Gutschrift-Abwesenheit mit gestempelter Zeit kollidiert — laut Erfassung
   *  war der Mitarbeiter gleichzeitig abwesend UND anwesend.
   *  ⚠️ Es wird NICHTS gedeckelt (der frühere Deckel war ein Fehler und wurde zurückgenommen): gerechnet wird
   *  ehrlich, was erfasst wurde, und der Widerspruch geht an den Menschen, der ihn auflösen kann. Einer der
   *  beiden Einträge gehört korrigiert — meist hätte die Abwesenheit ein halber Tag sein müssen. */
  absenceStampConflicts?: string[]
  /** JArbSchG-Wochen-/Monatsregeln (Azubi-Block) — der schicht-basierte Report kann sie strukturell nicht
   *  sehen, weil er Schichten auf ihren Starttag bucht und keine Woche kennt. */
  periodFlags?: ArbZgFlag[]
  /** NUR ein Kennzeichen für die § 15-BBiG-Fußnote — nie der Ausbildungszeitraum selbst. Der ist chef-only
   *  (`team:bearbeiten`), während `arbeitszeit:lesen` keinen Rang-Floor hat. */
  ausbildungAktiv?: boolean
}

/**
 * § 3 ArbZG-Durchschnitt (docs/DESIGN-zeit-arbzg-durchschnitt.md). Antwort von GET timesheet/durchschnitt.
 * Der Rechenweg reist vollständig mit: bei einer Zahl, die jemanden in die Bußgeldzone bringt, ist eine
 * nicht nachvollziehbare Anzeige wertlos.
 */
export interface ArbZgAverageResponse {
  user: { id: number; name: string }
  /** Bezugstag (Fensterende) — der Tag, auf den die Rechnung zurückblickt. */
  today: string
  /** Festgelegter Ausgleichszeitraum der Werkstatt (§ 7); null = nicht festgelegt, dann beide Fenster. */
  ausgleichszeitraum: string | null
  guard: AverageGuardResult
  /** Ohne hinterlegtes Bundesland fehlt der Feiertagskalender — dann ist das Budget ZU GROSS gerechnet. */
  federalStateMissing: boolean
}

// ---- Abwesenheiten (Phase 2, Block 1 — docs/DESIGN-zeit-abwesenheiten.md) ----
export interface AbsenceTypeDTO {
  id: number
  key: string
  label: string
  effect: AbsenceEffect
  active: number
}
export interface AbsenceDTO {
  id: number
  userId: number
  /** Nur in Werkstatt-Sichten (Posteingang/Kalender) gefüllt. */
  userName?: string | null
  typeKey: string
  label: string
  effect: AbsenceEffect
  fromDay: string
  toDay: string
  halfDay: number
  status: 'offen' | 'genehmigt' | 'abgelehnt' | 'storniert'
  note: string | null
  requestedAt: string
  decidedBy: number | null
  decidedAt: string | null
  decisionReason: string | null
  version: number
  /** Herkunfts-Serie (Azubi-Block) — die UI gruppiert danach statt 40 Einzelzeilen zu zeigen. */
  seriesId?: number | null
  /** 'YYYY-MM' eines bereits abgeschlossenen Monats, in den nachgetragen wurde (sichtbare Narbe). */
  lateForPeriod?: string | null
}
export interface AbsenceTypesResponse {
  types: AbsenceTypeDTO[]
}
export interface AbsenceSeriesDTO {
  id: number
  userId: number
  userName?: string | null
  typeKey: string
  label: string
  /** 0=Mo … 6=So (Dienstplan-Konvention). */
  weekdays: number[]
  fromDay: string
  toDay: string
  halfDay: number
  exceptions: Array<{ from: string; to: string }>
  note: string | null
  plannedCount: number
  skippedCount: number
  createdAt: string
}
export interface CreateAbsenceSeriesRequest {
  type_id: number
  user_id: number
  /** 0=Mo … 6=So, 1–2 Einträge (Teilzeit-Berufsschule; Blockunterricht läuft über den Mehrtages-Antrag). */
  weekdays: number[]
  from_day: string
  to_day: string
  half_day?: boolean
  /** Ferien/Praktikumsblöcke. */
  exceptions?: Array<{ from: string; to: string }>
  note?: string
  client_uuid: string
}
export interface CreateAbsenceSeriesResponse {
  series: AbsenceSeriesDTO
  created: number
  /** true = die Serie existierte bereits (Replay derselben client_uuid). Muss vom Server kommen: `created`
   *  allein ist mehrdeutig — 0 heißt auch „alle Tage kollidierten mit bestehenden Einträgen". */
  idempotent?: boolean
  /** Tage, die nicht (oder ohne Wirkung) angelegt wurden — ehrlicher Teilerfolg statt stiller Lücke. */
  skipped: Array<{ day: string; reason: 'overlap' | 'kein_solltag' }>
}
export interface AbsenceSeriesListResponse {
  series: AbsenceSeriesDTO[]
}
export interface CancelAbsenceSeriesRequest {
  /** Ab wann storniert wird (Default: heute) — Vergangenheit bleibt stehen. */
  from_day?: string
}
export interface AbsencesResponse {
  absences: AbsenceDTO[]
}
export interface CreateAbsenceRequest {
  type_id: number
  from_day: string
  to_day: string
  /** Nur bei Eintages-Anträgen (from_day == to_day). */
  half_day?: boolean
  note?: string
  client_uuid?: string
  /** Chef-Direkteintrag für einen Mitarbeiter (z. B. Krankmeldung) — entsteht sofort als 'genehmigt'. */
  user_id?: number
}
export interface DecideAbsenceRequest {
  status: 'genehmigt' | 'abgelehnt'
  /** Pflicht bei Ablehnung (nachvollziehbare Entscheidung). */
  decision_reason?: string
  version: number
}
export interface CancelAbsenceRequest {
  version: number
}

// ---- Urlaubskonto (Phase 2/2a, BUrlG — docs/DESIGN-zeit-urlaubskonto.md) ----
/** `GET /vacation?year=&userId=`: configured=false, solange kein Jahresanspruch gepflegt ist. */
export interface VacationAccountResponse {
  year: number
  configured: boolean
  account: {
    entitlementTenths: number
    carryTenths: number
    consumedTenths: number
    remainingTenths: number
    prorated: boolean
  } | null
  /** 2b: jüngster UNBESTÄTIGTER Verfall-Hinweis — die Urlaubskarte zeigt das Bestätigungs-Banner. */
  pendingNotice?: VacationNoticeDTO | null
}

/** Ein protokollierter Verfall-Hinweis (write-once; restTenths = Snapshot beim Versand). */
export interface VacationNoticeDTO {
  id: number
  year: number
  restTenths: number
  sentAt: string
  acknowledgedAt: string | null
}
/** `GET /vacation/assistant?year=` (Freigabe-Rolle): je MA mit Konto Rest/Übertrag + jüngster Hinweis. */
export interface VacationAssistantResponse {
  year: number
  rows: Array<{
    userId: number
    name: string
    remainingTenths: number
    carryTenths: number
    lastNotice: VacationNoticeDTO | null
  }>
}
export interface SendVacationNoticeRequest {
  user_id: number
  year?: number
  /** Write-Contract: derselbe Klick darf per Netz-Retry keine zweite Protokoll-Zeile erzeugen. */
  client_uuid?: string
}
export interface ForfeitVacationRequest {
  user_id: number
}

// ---- Dienstplan (W9.5; versioniert seit Phase-1/1b) ----
/** `GET /workplans/:id`: ALLE Dienstplan-Versionen eines Nutzers (Mo..So; Zeilen tragen validFrom).
 *  Wirksam am Tag D ist die Version mit dem größten validFrom ≤ D — Auflösung via @wf/shared
 *  effectiveWorkPlan/sollSecondsForDay (eine Wahrheit für Backend + Frontend). */
export interface WorkPlanResponse {
  userId: number
  plan: WorkPlanDay[]
}
/** Eine Dienstplan-Tageseingabe (`PUT /workplans`). start/end/break in Minuten; start/end null → kein Solltag
 *  (der Tag fehlt dann im Versions-Schnappschuss). */
export interface WorkPlanDayInput {
  weekday: number
  startMin?: number | null
  endMin?: number | null
  breakMin?: number | null
}
/** `PUT /workplans` (planung|chef|einstellungen): schreibt eine Dienstplan-VERSION — `days` ist der komplette
 *  Wochen-Schnappschuss, gültig ab `validFrom` ('YYYY-MM-DD'). Ohne validFrom: Ersteinrichtung = heute, sonst
 *  1. des Folgemonats (laufender Monat bleibt stabil). Vergangene Versionen sind unveränderlich (400). */
export interface SetWorkPlanRequest {
  userId: number
  days: WorkPlanDayInput[]
  validFrom?: string
}

// ---- Zeitkonto / Monatsabschluss (Phase-1/Teilschritt 2, GoBD-Befund A1) ----
/** Eine ABGESCHLOSSENE (eingefrorene) Zeitkonto-Periode. Ein offener Monat hat KEINE Zeile — er wird live
 *  gerechnet; die Zeile entsteht erst mit dem Hard-Close und ist danach unveränderlich (DB-Trigger; „Wieder
 *  öffnen" = Löschen der jüngsten Zeile, nur binnen 24 h nach dem Abschluss). Alle Werte in Integer-MINUTEN. */
export interface TimeAccountPeriodDTO {
  period: string // 'YYYY-MM'
  openingBalanceMin: number
  sollMin: number
  istMin: number
  /** closing = opening + ist − soll (kann negativ sein). */
  closingBalanceMin: number
  closedAt: string
  /** null = automatischer Monats-Cron. */
  closedBy: number | null
  closedByName?: string | null
}

/** `GET /timesheet/account?userId=` — Zeitkonto eines Mitarbeiters: abgeschlossene Perioden (neueste zuerst)
 *  + der nächste abschließbare Monat (null = aktuell nichts abschließbar). */
export interface TimeAccountResponse {
  userId: number
  periods: TimeAccountPeriodDTO[]
  /** Nächster abschließbarer Monat ('YYYY-MM'): Folgemonat des letzten Abschlusses bzw. der Vormonat beim
   *  ersten Abschluss — immer strikt vor dem laufenden Monat. */
  nextClosable: string | null
  /** Jüngste abgeschlossene Periode, solange ihr 24-h-Korrekturfenster noch offen ist (→ „Wieder öffnen"). */
  reopenablePeriod: string | null
}

/** `POST /timesheet/close` (arbeitszeit:bearbeiten): Monat eines Mitarbeiters abschließen (Hard-Close). */
export interface CloseTimeAccountRequest {
  userId: number
  period: string
}

/** `POST /timesheet/reopen` (arbeitszeit:bearbeiten): jüngsten Abschluss binnen 24 h zurücknehmen. */
export interface ReopenTimeAccountRequest {
  userId: number
  period: string
}

// ---- Chef-Statistik (W9.6) ----
/** Kennzahlen je Mitarbeiter. Sekunden via @wf/shared/time (B-5-Pairing), nicht die alte UTC-Tag-Gruppierung. */
export interface StatsMechanic {
  id: number
  name: string
  color: string
  position: string | null
  /** Anwesenheit im Zeitraum (Sekunden). */
  attSec: number
  /** Produktiv auf Aufträge gebuchte Zeit im Zeitraum (Sekunden). */
  jobSec: number
  /** Anwesenheit der letzten 7 Tage (Basis der Soll/Ist-Überstunden). */
  ist7Sec: number
  sollWeekMin: number
  /** jobSec/attSec in %, null ohne Anwesenheit. */
  productivity: number | null
  /** ist7Sec − sollWeekMin*60: + Über-, − Unterstunden (letzte 7 Tage vs. Wochen-Soll). */
  overUnderSec: number
}
export interface StatsWorkshop {
  bays: number
  totalScheduled: number
  /** Termine je Tagesstunde 0..23. */
  peakHours: number[]
  busiestHour: number | null
  busiestCount: number
  avgProductivity: number | null
  /** Auslastung in % (verplante Slot-Minuten / (bays × Öffnungsminuten × Tage), @wf/shared bayUtilization);
   *  null ohne Kapazität. Kann >100 sein (Überbuchung). Optional bis zum Frontend-Fixture-Nachzug. */
  utilizationPercent?: number | null
}
export interface StatsBusiness {
  ordersTaken: number
  ordersDone: number
  revenueCents: number
  revenueDoneCents: number
  avgOrderCents: number
  estApproved: number
  estDeclined: number
  avgLeadDays: number | null
}
/** `GET /stats?days` (team|chef|planung|einstellungen): Produktivität/Überstunden/Auslastung/Umsatz/KV-Quote. */
export interface StatsResponse {
  periodDays: number
  mechanics: StatsMechanic[]
  workshop: StatsWorkshop
  /** Umsatz/Betriebszahlen — nur für Träger des Rechts `umsatz` (Rang 90); sonst weggelassen (V2). */
  business?: StatsBusiness
}

// ---- Kunden & Fahrzeuge (= Frontend-Handoff Welle 5) ----
/** Kunde, wie ihn die API liefert (= Drizzle-Zeile customers, inkl. version für OCC). */
export interface CustomerDTO {
  id: number
  name: string
  phone: string | null
  email: string | null
  address: string | null
  createdAt: string
  workshopId: number | null
  clientUuid: string | null
  version: number
}
/** Fahrzeug, wie es die API liefert (= Drizzle-Zeile vehicles, inkl. version für OCC).
 *  Stammdaten aus dem Fahrzeugschein (Zulassungsbescheinigung Teil I), alle optional. */
export interface VehicleDTO {
  id: number
  customerId: number | null
  plate: string
  make: string | null // D.1 Marke
  model: string | null // D.3 Handelsbezeichnung / Modell
  vin: string | null // E FIN/VIN
  hsn: string | null // 2.1 Herstellerschlüsselnummer
  tsn: string | null // 2.2 Typschlüsselnummer
  firstRegistration: string | null // B Erstzulassung ('YYYY-MM-DD')
  displacementCcm: number | null // P.1 Hubraum (cm³)
  powerKw: number | null // P.2 Nennleistung (kW)
  fuelType: string | null // P.3 Kraftstoffart
  createdAt: string
  workshopId: number | null
  clientUuid: string | null
  version: number
}

/**
 * Kunden-LISTEN-Zeile: CustomerDTO + denormalisierte Zähler, die die Kundenliste ohne N+1 braucht.
 * Per korrelierter Subquery im Repo befüllt (mandantengescopt). Reine Anzeigefelder — schreibende Routen
 * nutzen weiter CustomerDTO/die Zeile (volle Zeile = OCC-Lese-Token).
 */
export interface CustomerListItemDTO extends CustomerDTO {
  /** Anzahl Fahrzeuge des Kunden (eigene Werkstatt). */
  vehicleCount: number
  /** Anzahl Aufträge des Kunden (eigene Werkstatt). */
  orderCount: number
  /** Anlagedatum des jüngsten Auftrags (MAX(created_at)) oder null, wenn keiner. */
  lastOrderAt: string | null
}

export interface CustomersListResponse {
  customers: CustomerListItemDTO[]
}
export interface CustomerResponse {
  customer: CustomerDTO
}
/** Kundendetail mit Fahrzeugen + Auftragshistorie (neueste zuerst, gedeckelt). */
export interface CustomerDetailResponse {
  customer: CustomerDTO
  vehicles: VehicleDTO[]
  orders: OrderDTO[]
}
/** Kunde anlegen (`client_uuid` = idempotent). Fahrzeuge werden separat über POST /vehicles angelegt;
 *  die gebündelte Erstanlage Kunde+Fahrzeug+Auftrag ist Sache des Order-Wizards (W7, verkettete Creates). */
export interface CreateCustomerRequest {
  name: string
  phone?: string | null
  email?: string | null
  address?: string | null
  client_uuid?: string
}
/** Kunde ändern — `version` (Lese-Token) Pflicht (Optimistic Concurrency). */
export interface PatchCustomerRequest {
  version: number
  name?: string
  phone?: string | null
  email?: string | null
  address?: string | null
}

export interface VehicleResponse {
  vehicle: VehicleDTO
}
export interface CreateVehicleRequest {
  customerId: number | null
  plate: string
  make?: string | null
  model?: string | null
  vin?: string | null
  hsn?: string | null
  tsn?: string | null
  firstRegistration?: string | null
  displacementCcm?: number | null
  powerKw?: number | null
  fuelType?: string | null
  client_uuid?: string
}
/** Fahrzeug ändern (inkl. Kunde umhängen) — `version` Pflicht (OCC). */
export interface PatchVehicleRequest {
  version: number
  plate?: string
  make?: string | null
  model?: string | null
  vin?: string | null
  hsn?: string | null
  tsn?: string | null
  firstRegistration?: string | null
  displacementCcm?: number | null
  powerKw?: number | null
  fuelType?: string | null
  customerId?: number | null
}
/** Auftragshistorie eines Fahrzeugs (neueste zuerst). */
export interface VehicleHistoryResponse {
  orders: OrderDTO[]
}

/** DSGVO-Auskunft (Art. 15): alle Daten EINES Kunden (read-only, mandantengescopt). */
export interface CustomerExportResponse {
  exportedAt: string
  type: string
  customer: CustomerDTO
  vehicles: VehicleDTO[]
  orders: OrderDTO[]
}
/**
 * Löschen blockiert: der Datensatz hat Aufträge (GoBD-Aufbewahrungspflicht) → kein Hard-Delete.
 * Die UI bietet stattdessen Anonymisierung (DSGVO-Löschpfad) an. KEIN stiller Datenverlust.
 */
export interface DeleteBlockedResponse {
  error: string
  blocked: 'has_orders'
}
/** OCC-Konflikt (409) beim Kunden-Update: aktueller Server-Stand + Version für die UI-Reconciliation. */
export interface CustomerConflictResponse {
  error: string
  conflict: true
  customer: CustomerDTO
  version: number
}
/** OCC-Konflikt (409) beim Fahrzeug-Update. */
export interface VehicleConflictResponse {
  error: string
  conflict: true
  vehicle: VehicleDTO
  version: number
}
/** Patch-Request für einen Auftrag — `version` (Lese-Token) ist Pflicht (Optimistic Concurrency).
 *  Abrechnungsfelder (estimateStatus/paid/paidMethod) brauchen das `rechnung`-Recht; nach
 *  Kundenfreigabe/Rechnung sind mileage/Positionen GoBD-gesperrt (kommen dann in `skipped` zurück).
 *  Planungsfelder (scheduledDate/startMin/endMin/bay) brauchen annahme- ODER belegung-Bearbeiten (Plantafel).
 *  Arbeitslohn ist KEIN Auftragsfeld mehr — er läuft als „Arbeit/Leistung"-Position (order_items). */
export interface PatchOrderRequest {
  version: number
  status?: string
  prio?: string
  mechanicId?: number | null
  note?: string | null
  scheduledDate?: string | null
  customerId?: number | null
  vehicleId?: number | null
  mileage?: number | null
  startMin?: number | null
  endMin?: number | null
  /** Arbeitsplatz (1..bays) der Plantafel; null räumt die Zuordnung. */
  bay?: number | null
  archived?: boolean | number
  estimateStatus?: string
  dueDay?: number | null
  /** Bezahlt-Schalter: true setzt paidAt (idempotent: behält ein bereits gesetztes Datum), false hebt auf. */
  paid?: boolean
  paidMethod?: string | null
}
export interface PatchOrderResponse {
  ok: true
  order: OrderDTO
  version: number
  skipped: string[]
}

// ---- Anlage-Wizard + auftragsgebundene Kinder (Welle 6.3) ----
/** Auftrag anlegen — optional mit initialen Aufgaben/Teilen/Positionen in EINEM Aufruf. `client_uuid`
 *  macht die Anlage idempotent (Offline-Replay legt keinen Doppel-Auftrag an). Antwort: { order } (201 neu / 200 Replay). */
export interface CreateOrderRequest {
  customerId?: number | null
  vehicleId?: number | null
  mechanicId?: number | null
  prio?: string
  note?: string | null
  scheduledDate?: string | null
  client_uuid?: string
  tasks?: { text: string }[]
  parts?: { name: string; partNumber?: string | null; qty?: number; priceCents?: number; vatRate?: number; status?: string }[]
  items?: { name: string; qty?: number; priceCents?: number; vatRate?: number; serviceId?: number | null }[]
}
/** Antwort der Kind-Mutationen (POST/PATCH/DELETE auf tasks/parts/items): der frische Auftrag + Kinder. */
export type OrderChildResponse = OrderDetailResponse
export interface AddTaskRequest {
  text: string
}
export interface PatchTaskRequest {
  text?: string
  done?: boolean
}
export interface AddPartRequest {
  name: string
  partNumber?: string | null
  qty?: number
  priceCents?: number
  /** USt-Satz in Prozent (19/7/0, Default 19). Bei `partId` kommt der Satz aus dem Lager-Teil. */
  vatRate?: number
  /** 'needed' | 'ordered' | 'delivered' | 'installed' (Default 'needed'). */
  status?: string
  /** Lager-Teil (inventory_parts.id): gesetzt → Bestand reservieren + Verkaufspreis als Snapshot übernehmen
   *  (Name/SNR/Preis kommen dann vom Lager-Teil); leer → Einmal-Teil (landet als order_oneoff im Einkauf-Board). */
  partId?: number | null
}
export interface PatchPartRequest {
  name?: string
  partNumber?: string | null
  qty?: number
  priceCents?: number
  vatRate?: number
  status?: string
}
export interface AddItemRequest {
  name: string
  qty?: number
  priceCents?: number
  vatRate?: number
  serviceId?: number | null
}
export interface PatchItemRequest {
  name?: string
  qty?: number
  priceCents?: number
  vatRate?: number
}

// ---- Offene Posten & Rechnungsliste (Welle 6.4, Alt-Parität /orders/open + /orders/invoices) ----
/** Offener Posten: fertiger/abgeholter, noch nicht bezahlter Auftrag mit Auftragswert (Teile + Positionen; Lohn ist eine Position). */
export interface OpenItemDTO {
  id: number
  nr: string | null
  status: string
  finishedAt: string | null
  invoicedAt: string | null
  invNo: string | null
  createdAt: string
  plate: string | null
  vehicleModel: string | null
  customerName: string | null
  /** Auftragswert in ganzen Cent: Σ Teile + Σ Positionen (Arbeitslohn ist eine Position). */
  valueCents: number
}
export interface OpenItemsResponse {
  items: OpenItemDTO[]
  totalCents: number
  count: number
}
/** Rechnungsliste (aus dem Ledger): je Zeile ein Beleg (Rechnung ODER Stornorechnung). */
export interface InvoiceDTO {
  id: number
  /** Belegart: normale Rechnung oder Stornorechnung. */
  type: InvoiceType
  /** true, wenn diese (Original-)Rechnung durch eine Stornorechnung aufgehoben wurde (abgeleitet). */
  cancelled: boolean
  orderId: number
  nr: string | null
  invNo: string | null
  invSeq: number | null
  invYear: number | null
  invoicedAt: string | null
  status: string
  paidAt: string | null
  paidMethod: string | null
  plate: string | null
  customerName: string | null
  /** Bruttowert des Belegs in Cent (bei Stornorechnung negativ). */
  valueCents: number
}
export interface InvoicesResponse {
  invoices: InvoiceDTO[]
  count: number
  totalCents: number
  paidCents: number
}

// ---- Rechnungs-Ledger (Baustein B): eingefrorener Beleg-Snapshot (GoBD) ----
export type InvoiceType = 'invoice' | 'cancellation'
/** Eine eingefrorene Rechnungs-Position (Snapshot vom Auftrag, Werte kopiert). */
export interface InvoiceLineDTO {
  position: number
  itemKind: 'part' | 'labor' | 'misc'
  description: string
  articleNumber: string | null
  quantity: number
  unit: string
  unitPriceNet: number
  vatRate: number
  lineNet: number
  lineTax: number
  lineGross: number
}
/** Eingefrorener Kunden-Snapshot im Rechnungskopf (kein FK auf die lebende customers-Zeile). */
export interface InvoiceCustomerSnapshot {
  name: string
  address: string | null
  phone: string | null
  email: string | null
}
/** Eingefrorener Auftrags-/Fahrzeug-Kontext im Rechnungskopf. */
export interface InvoiceSubjectSnapshot {
  orderNr: string | null
  plate: string | null
  vehicleModel: string | null
  mileage: number | null
  tasks: string[]
}
/** Vollständiger, eingefrorener Beleg (Anzeige/Druck). `seller` = Firmendaten-Snapshot (BillingDTO). */
export interface InvoiceDocDTO {
  id: number
  orderId: number
  invoiceNumber: string
  type: InvoiceType
  /** Nur bei type='invoice': true, wenn eine Stornorechnung diese aufhebt. */
  cancelled: boolean
  relatedInvoiceId: number | null
  relatedInvoiceNumber: string | null
  issueDate: string
  taxScheme: string
  netTotal: number
  taxTotal: number
  grossTotal: number
  customer: InvoiceCustomerSnapshot
  seller: BillingDTO
  subject: InvoiceSubjectSnapshot
  lines: InvoiceLineDTO[]
  createdAt: string
}
export interface InvoiceDocResponse {
  invoice: InvoiceDocDTO
}

// ---- Abo / Tarif (Cevio-Abo der Werkstatt) ----
/** Ein wählbarer Tarif mit Preisen (netto/brutto in Cent) je Intervall — für den „Tarif ändern"-Dialog. */
export interface PlanOption {
  tier: PlanTier
  userLimit: number
  monthly: { net: number; gross: number }
  yearly: { net: number; gross: number }
}

/** `POST /billing/change-plan` (Chef) — Tarifwechsel-Wunsch. `version` = zuletzt gelesene Abo-Version (OCC). */
export interface ChangePlanRequest {
  planTier: PlanTier
  billingInterval: BillingInterval
  version: number
}

/**
 * `GET /billing/status` (Chef) — Tarif-Modell-Statusansicht. Bei Bestandswerkstatt ohne Abo-Zeile liefert der
 * Server stattdessen `{ provisioned: false, accessLevel: 'full', seatsUsed }` (Teilmenge; hier nicht getypt).
 */
export interface BillingStatusResponse {
  provisioned: boolean
  billingStatus: BillingStatus
  accessLevel: AccessLevel
  /** null = noch kein Tarif gewählt (gratis/Testkonto). */
  planTier: PlanTier | null
  billingInterval: BillingInterval
  priceCentsNet: number
  priceCentsGross: number
  seatsUsed: number
  userLimit: number
  scheduledPlanTier: PlanTier | null
  scheduledBillingInterval: BillingInterval | null
  trialEnd: string | null
  currentPeriodEnd: string | null
  pastDueSince: string | null
  cancelAt: string | null
  mandatePresent: boolean
  provider: string
  version: number
  availablePlans: PlanOption[]
}

// ---- Abo-Rechnungen (Cevio → Werkstatt; monatliche SEPA-Lastschrift-Belege) ----
/** Rechnungssteller-Snapshot (Cevio) auf einer Abo-Rechnung — eingefroren zum Ausstellungszeitpunkt. */
export interface AboInvoiceSellerSnapshot {
  name: string
  legalName: string | null
  address: string
  zip: string
  city: string
  country: string
  taxNumber: string | null
  vatId: string | null
  email: string | null
  sepaCreditorId: string | null
}
/** SEPA-Lastschrift-Angaben (Pflicht für die Vorabankündigung/Pre-Notification) auf der Abo-Rechnung. */
export interface AboInvoiceSepa {
  creditorId: string | null
  mandateReference: string | null
  /** Voraussichtliches Einzugsdatum (ISO-8601 UTC) — null, falls (noch) nicht bekannt. */
  collectionDate: string | null
}
/** Eine Zeile in der Abo-Rechnungsliste auf „Abo & Lizenzen". */
export interface AboInvoiceDTO {
  id: number
  invoiceNumber: string
  issueDate: string
  periodStart: string
  periodEnd: string
  seats: number
  netTotal: number
  taxTotal: number
  grossTotal: number
  currency: string
}
export interface AboInvoiceListResponse {
  invoices: AboInvoiceDTO[]
  count: number
}
/** Vollständiger, eingefrorener Abo-Rechnungsbeleg (Druck/PDF). `buyer` = Firmendaten der Werkstatt (Snapshot). */
export interface AboInvoiceDocDTO {
  id: number
  invoiceNumber: string
  issueDate: string
  /** Leistungszeitraum (§14 Abs. 4 Nr. 6): der abgerechnete Abo-Monat. */
  periodStart: string
  periodEnd: string
  seats: number
  unitPriceCentsNet: number
  /** Netto-Zuschlag (Cent) aus untermonatlichen Seat-Zugängen der Vorperiode — eigene Rechnungszeile. 0 = keiner. */
  addonNet: number
  vatRate: number
  netTotal: number
  taxTotal: number
  grossTotal: number
  currency: string
  seller: AboInvoiceSellerSnapshot
  buyer: BillingDTO
  sepa: AboInvoiceSepa
}
export interface AboInvoiceDocResponse {
  invoice: AboInvoiceDocDTO
}

// ---- Rechnung festschreiben / stornieren / Kostenvoranschlag (Welle D + Baustein B) ----
/** Antwort auf `POST /orders/:id/invoice` (Festschreiben) und `POST /orders/:id/storno` (Storno):
 *  der frische Beleg + der aktualisierte Auftrag (Spiegel-Felder). `already`=true, wenn schon vorhanden
 *  (Festschreiben ist idempotent; nie eine neue Nummer bei bestehender aktiver Rechnung). */
export interface InvoiceResponse {
  order: OrderDTO
  invoice: InvoiceDocDTO
  already: boolean
}
/** Antwort auf `POST /orders/:id/estimate` — der KLARTEXT-Freigabe-Token (nur hier; DB speichert nur den Hash). */
export interface SendEstimateResponse {
  order: OrderDTO
  token: string
}
/** Öffentliche KV-Ansicht (`GET /public/estimate/:token`, ohne Login) — bewusst reduziert, keine Secrets. */
export interface PublicEstimateOrder {
  nr: string | null
  plate: string | null
  vehicleModel: string | null
  customerName: string | null
  mileage: number | null
  estimateStatus: string
  approvedAt: string | null
  partsCents: number
  itemsCents: number
  /** Netto/USt/Brutto über @wf/shared (Kleinunternehmer-aware). */
  netCents: number
  vatCents: number
  grossCents: number
  /** USt-Aufschlüsselung je Satz (Bemessungsgrundlage + Steuer); leer bei Kleinunternehmer. */
  byRate: VatRateGroup[]
  kleinunternehmer: boolean
}
export interface PublicEstimateLinePart {
  partNumber: string | null
  name: string
  priceCents: number
  vatRate: number
  qty: number
}
export interface PublicEstimateLineItem {
  name: string
  priceCents: number
  vatRate: number
  qty: number
}
export interface PublicEstimateAnbieter {
  name: string
  ceo: string | null
  address: string | null
  zip: string | null
  city: string | null
  taxNumber: string | null
  vatId: string | null
  invPhone: string | null
  invEmail: string | null
}
export interface PublicEstimateResponse {
  order: PublicEstimateOrder
  tasks: string[]
  parts: PublicEstimateLinePart[]
  items: PublicEstimateLineItem[]
  anbieter: PublicEstimateAnbieter
}
/** Antwort auf `POST /public/estimate/:token/approve|decline`. */
export interface PublicEstimateDecisionResponse {
  ok: true
  status: 'approved' | 'declined'
}

// ---- Einstellungen / §14-Firmendaten (Welle D) — operiert auf der workshops-Zeile des Mandanten ----
/** §14-Firmenstammdaten + Kleinunternehmer-Flag (Feldnamen = workshops-Spalten). Nur für Büro-Rollen sichtbar. */
export interface BillingDTO {
  name: string
  legalName: string | null
  ceo: string | null
  address: string | null
  zip: string | null
  city: string | null
  taxNumber: string | null
  vatId: string | null
  /** 0/1 (§19 Kleinunternehmer: keine USt ausweisen + Pflichthinweis). */
  kleinunternehmer: number
  iban: string | null
  bank: string | null
  invPhone: string | null
  invEmail: string | null
}
/** `GET /settings`. `billing` ist null, wenn der Nutzer keine Berechtigung für Firmen-/Bankdaten hat.
 *  `laborRateCents` ist null, wenn der Nutzer keine Preisberechtigung hat (Rechnung/Leistungen/Einstellungen). */
export interface SettingsResponse {
  bays: number
  autoLogoutMin: number
  laborRateCents: number | null
  /** Bundesland-Kürzel (BW/BY/…) für den Feiertagskalender im Monats-Soll; null = noch nicht gesetzt. */
  federalState?: string | null
  /** Gemeinde-abhängige Feiertage zugeschaltet (Fronleichnam SN/TH, Mariä Himmelfahrt BY). */
  holidayCatholicMunicipality?: boolean
  /** DATEV-Fundament (Stufe A): Berater- (4–7 Ziffern) / Mandantennummer (1–5 Ziffern). */
  beraternummer?: string | null
  mandantennummer?: string | null
  /** Ausgleichszeitraum nach § 3 Satz 2 ArbZG ('24_wochen' | '6_monate'); null = nicht festgelegt, dann
   *  rechnet der Durchschnittswächter beide gesetzlichen Varianten. */
  ausgleichszeitraum?: string | null
  /** Öffnungszeit-Fenster (Minuten ab Mitternacht) — Zeitachse + Kapazität der Belegungs-Plantafel.
   *  Optional (nicht required), bis der Frontend-Handoff die eingefrorenen Fixtures nachzieht. */
  openStartMin?: number
  openEndMin?: number
  billing: BillingDTO | null
}
// ---- Preiskatalog / Standard-Leistungen (Welle B) ----
/** Eine Katalog-Leistung mit Festpreis (= Drizzle-Zeile services, Anzeige-Teilmenge). */
export interface ServiceDTO {
  id: number
  name: string
  /** Verkaufspreis netto in Cent. */
  priceCents: number
  /** Einkaufspreis netto in Cent (z. B. fremdbezogene Leistung); Marge = priceCents − purchasePriceCents. */
  purchasePriceCents: number
  /** USt-Satz in Prozent (19/7/0) — wird beim Übernehmen als Snapshot auf die Auftrags-Position kopiert. */
  vatRate: number
  unit: string | null
  category: string | null
}
export interface ServicesResponse {
  services: ServiceDTO[]
}
export interface ServiceResponse {
  service: ServiceDTO
}
export interface CreateServiceRequest {
  name: string
  priceCents?: number
  purchasePriceCents?: number
  vatRate?: number
  unit?: string | null
  category?: string | null
}
export interface PatchServiceRequest {
  name?: string
  priceCents?: number
  purchasePriceCents?: number
  vatRate?: number
  unit?: string | null
  category?: string | null
}

// ---- Teile-Einkaufsliste + Teile-Gedächtnis (Welle B) ----
/** Eine Teile-Zeile in der werkstattweiten Einkaufsliste: Teil + denormalisierter Auftrags-/Anlage-Kontext. */
export interface PartListItemDTO extends PartDTO {
  orderNr: string | null
  plate: string | null
  vehicleModel: string | null
  addedByName: string | null
}
export interface PartsListResponse {
  parts: PartListItemDTO[]
}
/** Antwort des Teile-Gedächtnisses (`GET /parts/lookup?num=`): letzter Treffer dieser Nummer in der Werkstatt. */
export interface PartLookupResponse {
  found: boolean
  partNumber?: string
  name?: string
  priceCents?: number
}

/** `PATCH /settings` (cap `einstellungen`). Alle Felder optional; Bankdaten nur über dieses Recht schreibbar. */
export interface PatchSettingsRequest {
  name?: string
  bays?: number
  /** Öffnungszeit-Fenster (Minuten ab Mitternacht, 0..1440, Ende > Start — Paar wird serverseitig geprüft). */
  openStartMin?: number
  openEndMin?: number
  autoLogoutMin?: number
  laborRateCents?: number
  /** Bundesland-Kürzel (BW/BY/…) für den Feiertagskalender; '' setzt zurück auf null. */
  federalState?: string | null
  /** § 3 Satz 2 ArbZG: '24_wochen' | '6_monate'; '' bzw. null → nicht festgelegt. */
  ausgleichszeitraum?: string | null
  holidayCatholicMunicipality?: boolean
  kleinunternehmer?: boolean | number
  legalName?: string | null
  ceo?: string | null
  address?: string | null
  zip?: string | null
  city?: string | null
  taxNumber?: string | null
  vatId?: string | null
  iban?: string | null
  bank?: string | null
  invPhone?: string | null
  invEmail?: string | null
  beraternummer?: string | null
  mandantennummer?: string | null
}

// ---- Lager (Inventar) + Beschaffung (Einkauf) ----
// Geld in ganzen Cent (*Cents), Mengen in ganzen Zehnteln (*Tenths, 1 NK) — siehe @wf/shared/inventory.
/** Lager-Stammteil (= Drizzle-Zeile inventory_parts, inkl. version für OCC). */
export interface InventoryPartDTO {
  id: number
  workshopId: number
  partNumber: string
  partNumberNorm: string
  name: string
  unit: string
  stockTenths: number
  safetyStockTenths: number
  purchasePriceCents: number
  sellingPriceCents: number
  vatRate: number
  manufacturer: string | null
  storageLocation: string | null
  barcode: string | null
  isActive: number
  clientUuid: string | null
  version: number
  createdAt: string
  updatedAt: string
}
/** Kritisches Teil (Bestand < Sicherheitsbestand) + Flag, ob bereits ein offenes restock-Item läuft
 *  (dann zeigt die UI "in Bestellung" statt des "Zu bestellen"-Buttons). */
export interface CriticalPartDTO extends InventoryPartDTO {
  /** 0/1 — es existiert ein offenes restock-procurement_item (Status needed/ordered) für dieses Teil. */
  openRestock: number
}
/** Beschaffungs-Eintrag des Einkauf-Boards (= Drizzle-Zeile procurement_items). */
export interface ProcurementItemDTO {
  id: number
  workshopId: number
  partId: number | null
  partNumber: string | null
  name: string
  unit: string | null
  quantityTenths: number
  source: string
  status: string
  orderId: number | null
  /** Verknüpfte Auftrags-Position (parts.id) bei source='order_oneoff' — teilt den Status mit dieser Zeile. */
  orderPartId: number | null
  clientUuid: string | null
  createdAt: string
  updatedAt: string
}
/** Board-Zeile: ProcurementItem + denormalisierter Auftrags-Kontext (für order_oneoff-Karten). */
export interface ProcurementListItemDTO extends ProcurementItemDTO {
  orderNr: string | null
  plate: string | null
  vehicleModel: string | null
}

export interface InventoryListResponse {
  parts: InventoryPartDTO[]
}
export interface InventoryPartResponse {
  part: InventoryPartDTO
}
export interface CriticalPartsResponse {
  parts: CriticalPartDTO[]
}
/** Antwort beim Anlegen, wenn die SNR zu einem ARCHIVIERTEN Teil gehört: kein Fehler, sondern Reaktivierungs-Angebot.
 *  Die UI zeigt das Pop-up "Teil aus Archiv reaktivieren?" und ruft bei Bestätigung POST /inventory/:id/reactivate. */
export interface InventoryPartArchivedResponse {
  archived: true
  part: InventoryPartDTO
}
/** OCC-Konflikt (409) beim Stammdaten-Update eines Lager-Teils. */
export interface InventoryPartConflictResponse {
  error: string
  conflict: true
  part: InventoryPartDTO
  version: number
}
/** SNR-Prüfung (GET /inventory/check?num=) für das Anlege-Popup / die Auftrags-Live-Suche. */
export interface SnrCheckResponse {
  status: 'free' | 'active' | 'archived'
  part: InventoryPartDTO | null
}
export interface ProcurementListResponse {
  items: ProcurementListItemDTO[]
}
export interface ProcurementItemResponse {
  item: ProcurementItemDTO
}

/** Lager-Teil anlegen. `client_uuid` = idempotent. Mengen in Zehnteln, Geld in Cent. */
export interface CreateInventoryPartRequest {
  partNumber: string
  name: string
  unit?: string
  stockTenths?: number
  safetyStockTenths?: number
  purchasePriceCents?: number
  sellingPriceCents?: number
  vatRate?: number
  manufacturer?: string | null
  storageLocation?: string | null
  barcode?: string | null
  client_uuid?: string
}
/** Lager-Teil ändern — `version` (Lese-Token) Pflicht (OCC). Bestand wird NICHT hier gesetzt,
 *  sondern über die Buchungs-Endpunkte (adjust/withdraw/add), damit jede Änderung im stock_log landet. */
export interface PatchInventoryPartRequest {
  version: number
  partNumber?: string
  name?: string
  unit?: string
  safetyStockTenths?: number
  purchasePriceCents?: number
  sellingPriceCents?: number
  vatRate?: number
  manufacturer?: string | null
  storageLocation?: string | null
  barcode?: string | null
}
/** Teil aus dem Archiv reaktivieren (is_active=1); optional Stammdaten gleich aktualisieren. */
export interface ReactivateInventoryPartRequest {
  partNumber?: string
  name?: string
  unit?: string
  safetyStockTenths?: number
  purchasePriceCents?: number
  sellingPriceCents?: number
  vatRate?: number
  manufacturer?: string | null
  storageLocation?: string | null
  barcode?: string | null
}
/** Manuelle absolute Bestandskorrektur (adjustStock) — setzt den Bestand auf stockTenths, Grund manual_adjustment. */
export interface AdjustStockRequest {
  stockTenths: number
}
/** Manuelle Entnahme/Zuführung (withdrawStock/addStock) — Menge immer positiv, Richtung ergibt sich aus der Route. */
export interface StockMoveRequest {
  quantityTenths: number
}
/** Nachschub aus dem Inventar anstoßen ("Zu bestellen") → legt ein restock-procurement_item an. */
export interface CreateRestockRequest {
  partId: number
  /** Bestellmenge in Zehnteln; fehlt sie, schlägt der Server safety_stock − stock vor. */
  quantityTenths?: number
}
/** Status eines Beschaffungs-Eintrags weiterschalten (advanceStatus). */
export interface AdvanceProcurementRequest {
  status: string
}

// ---- Betreiber-/Super-Admin: App-Verwaltung (mandantenübergreifend, nur is_superadmin) ----
/** Werkstatt-Zeile der Betreiber-Übersicht/-Detail, angereichert um Lizenz-/Nutzungs-Infos. */
export interface AdminWorkshopDTO {
  id: number
  name: string
  plan: string | null
  /** Lizenz-Status (V2-Statusmodell 13.07.2026; Historie der Wechsel steht im audit_log). */
  status: WorkshopStatus
  bays: number | null
  contact: string | null
  createdAt: string
  /** Letzte Aktivität irgendeines Werkstatt-Nutzers (max sessions.lastSeen) — für „Letzter Login"/Inaktiv-Warnung. */
  lastSeenAt: string | null
  avvAcceptedAt: string | null
  avvVersion: string | null
  /** Aktive Nutzer (ohne Super-Admin). */
  userCount: number
  /** Nutzerlimit des aktuellen Tarifs (aus PLAN_USER_LIMIT). */
  userLimit: number
  /** Primärer Chef-Username (für Anzeige / Passwort-Reset). */
  chefUsername: string | null
}
export interface AdminWorkshopsResponse {
  workshops: AdminWorkshopDTO[]
}
/** SaaS-KPIs fürs Betreiber-Dashboard (GET /admin/stats). Live gerechnet — bei der aktuellen
 *  Mandantenzahl sind das wenige Index-Scans; eine gecachte Stats-Tabelle lohnt erst ab ~Hunderten
 *  Werkstätten (bewusste YAGNI-Entscheidung 13.07.2026). Geld in ganzen Cent. */
export interface AdminStatsResponse {
  workshopsTotal: number
  byStatus: { active: number; suspended: number; canceled: number }
  /** Aktive Werkstätten mit Tarifpreis > 0 (Test/Probe/Owner zählen nicht). */
  payingCount: number
  mrrCents: number
  /** Umsatz je zahlendem Account (MRR/payingCount), 0 wenn keine zahlenden. */
  arpaCents: number
  /** MRR-Breakdown je Tarif (nur aktive Werkstätten), absteigend nach MRR. */
  byPlan: { plan: string; count: number; mrrCents: number }[]
  /** Werkstätten, die in den letzten 30/90 Tagen angelegt wurden (Wachstums-Proxy, solange es keine Abrechnungs-Historie gibt). */
  newWorkshops30d: number
  newWorkshops90d: number
  /** Nutzer (ohne Superadmin) mit Session-Aktivität heute / in den letzten 7 Tagen (DAU/WAU-Proxy über sessions.lastSeen). */
  activeUsersToday: number
  activeUsers7d: number
}
/** Mitarbeiter (sanitiert — nie Hash/Salt/2FA-Secret) im Werkstatt-Detail. */
export interface AdminWorkshopMember {
  id: number
  name: string
  username: string
  email: string | null
  position: string | null
  role: string
  active: number
  totpEnabled: number
  /** Effektive Caps (aus permissions geparst) — füllt das Bearbeiten-Formular vor. Authz-Config, keine PII. */
  caps: string[]
}
export interface AdminWorkshopDetailResponse {
  workshop: AdminWorkshopDTO
  users: AdminWorkshopMember[]
}
/** Werkstatt-Lizenz anlegen (Werkstatt + erster Chef). `plan` muss ein bekannter Tarif sein. */
export interface CreateWorkshopRequest {
  workshop_name: string
  chef_name: string
  username: string
  /** @deprecated Wird vom Backend IGNORIERT. Der Betreiber vergibt NIE ein Chef-Passwort — der Chef setzt es selbst
   *  per Aktivierungscode (siehe `activation` in der Antwort). Feld bleibt optional, bis die Frontend-Lane das
   *  Passwort-Eingabefeld entfernt (additiver Contract → eingefrorene Frontend-Tests bleiben grün). */
  password?: string
  plan?: string
  email?: string
}
export interface CreateWorkshopResponse {
  ok: true
  workshop: AdminWorkshopDTO
  chefUsername: string
  /** Chef-Aktivierung: Klartext-Code GENAU EINMAL (danach liegt nur der Hash in der DB) + Ablauf (72 h). Der Chef
   *  löst ihn öffentlich ein (POST onboarding/reset) und setzt sein Passwort selbst — bis dahin ist das Chef-Konto
   *  nicht einloggbar (Platzhalter-Hash). Das Frontend baut daraus den Magic-Link /aktivieren?code=…. */
  activation: { code: string; expiresAt: string }
}
/** Werkstatt-Lizenz/-Stammdaten ändern (V1 saveWorkshop: Name/Plan/Kontakt/aktiv + Hebebühnen). */
export interface UpdateWorkshopRequest {
  name?: string
  contact?: string | null
  plan?: string
  /** Lizenz-Status setzen (sperren/freigeben/kündigen). Sperrt/entsperrt Login UND laufende Sessions sofort. */
  status?: WorkshopStatus
  bays?: number
}
export interface UpdateWorkshopResponse {
  ok: true
  workshop: AdminWorkshopDTO | undefined
  /** 'over_limit', wenn nach einem Downgrade mehr aktive Nutzer als das neue Limit bestehen. */
  warning?: string
}
/** Chef-Zugang der Werkstatt zurücksetzen (Betreiber-Recovery / Re-Issue). Der Betreiber setzt KEIN Passwort mehr —
 *  der Reset stellt einen neuen Aktivierungscode aus, den der Chef selbst einlöst. */
export interface ResetChefPasswordRequest {
  /** @deprecated Wird vom Backend IGNORIERT (kein Betreiber-Passwort mehr). Feld bleibt optional, bis die
   *  Frontend-Lane das Passwort-Eingabefeld entfernt (additiver Contract → eingefrorene Tests bleiben grün). */
  password?: string
}
export interface ResetChefPasswordResponse {
  ok: true
  chefUsername: string
  /** Neuer Chef-Aktivierungscode (Klartext GENAU EINMAL) + Ablauf (72 h). Ein zuvor ausgegebener Code ist ab jetzt
   *  ungültig. Der Chef löst ihn öffentlich ein (POST onboarding/reset) und setzt sein Passwort selbst. */
  activation: { code: string; expiresAt: string }
}
export interface DeleteWorkshopResponse {
  ok: true
  deleted: { id: number; name: string }
}
/** Admin: Mitarbeiter in einer Werkstatt anlegen (workshop_id = Route-:id, korrekt zugeordnet). */
export interface AdminCreateUserRequest {
  name: string
  username: string
  password: string
  position?: string
  permissions?: string[]
  email?: string | null
  color?: string
}
export interface AdminCreateUserResponse {
  ok: true
  id: number
}
/** Admin: Mitarbeiter einer (fremden) Werkstatt ändern — alle Felder optional. */
export interface AdminPatchUserRequest {
  name?: string
  position?: string
  email?: string | null
  permissions?: string[]
  password?: string
  active?: boolean
}

// ---- Audit-Log-Viewer (16.07.): die append-only Nachweiskette (audit_log) LESBAR machen ----
/** Zeilen-Deckel je Abruf (GET /audit, GET /admin/audit). Bewusst KEINE Pagination (gibt es projektweit
 *  nicht) — bei Überlauf meldet `truncated: true` und die UI bittet, den Zeitraum enger zu wählen. */
export const AUDIT_PAGE_LIMIT = 500
/** Ein Eintrag der unveränderlichen Nachweiskette — um den Akteurs-Anzeigenamen angereichert. */
export interface AuditEntryDTO {
  id: number
  /** UTC 'YYYY-MM-DD HH:MM:SS' (DB-Format) — Anzeige lokalisiert das Frontend. */
  ts: string
  action: string
  targetType: string | null
  targetId: string | null
  detail: string | null
  actorUserId: number | null
  /** Anzeigename des Akteurs (JOIN zur Abfragezeit); null = System/ohne Login (z. B. Kunden-Freigabe)
   *  ODER gelöschter/anonymisierter Nutzer (dann bleibt nur die nackte ID — gewollt, Regel R9). */
  actorName: string | null
}
/** Gemeinsamer Antwort-Umschlag beider Audit-Sichten — EINE Wahrheit für truncated/from/to. */
export interface AuditEnvelope<E> {
  entries: E[]
  /** true = im Zeitraum lagen mehr als AUDIT_PAGE_LIMIT Zeilen — Zeitraum enger wählen. */
  truncated: boolean
  from: string
  to: string
}
/** GET /audit — Chef-Sicht, strikt auf die eigene Werkstatt gescopt (Recht audit:lesen, Rang-Floor 90). */
export type AuditResponse = AuditEnvelope<AuditEntryDTO>
/** Zeile der Betreiber-Globalsicht: zusätzlich der Mandant (null = plattformweite Betreiber-Aktion). */
export interface AdminAuditEntryDTO extends AuditEntryDTO {
  workshopId: number | null
  workshopName: string | null
}
/** GET /admin/audit — Betreiber-Globalsicht (requireSuperadmin), optional per workshopId gefiltert. */
export type AdminAuditResponse = AuditEnvelope<AdminAuditEntryDTO>
